"use client";

import { nearestLocality, searchLocalities, type Locality } from "@kedaat/locations";
import { useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { PinIcon } from "./icons";

/** Farther than this from every locality, the position is treated as outside Israel. */
const MAX_DISTANCE_KM = 25;

const formatDistance = (km: number) => (km < 1 ? "פחות מק״מ" : `${km.toFixed(1)} ק״מ`);

export function LocalityPicker({
  locality,
  onSelect,
}: {
  locality: Locality;
  onSelect: (locality: Locality) => void;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const results = useMemo(() => searchLocalities(query, 8), [query]);

  const choose = (l: Locality) => {
    onSelect(l);
    setQuery("");
    setOpen(false);
    setStatus(null);
    inputRef.current?.blur();
  };

  // The position is used only here, to find the nearest locality; it is never stored or sent.
  const locate = () => {
    if (!("geolocation" in navigator)) {
      setStatus("הדפדפן אינו תומך באיתור מיקום.");
      return;
    }
    setLocating(true);
    setStatus(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const nearest = nearestLocality(position.coords.latitude, position.coords.longitude);
        if (!nearest || nearest.distanceKm > MAX_DISTANCE_KM) {
          setStatus("לא נמצא יישוב בישראל קרוב למיקום שלך.");
          return;
        }
        setStatus(
          `היישוב הקרוב: ${nearest.locality.nameHe} (${formatDistance(nearest.distanceKm)})`,
        );
        onSelect(nearest.locality);
      },
      (error) => {
        setLocating(false);
        setStatus(
          error.code === error.PERMISSION_DENIED
            ? "לא ניתנה הרשאה לאיתור מיקום."
            : "לא הצלחנו לאתר את המיקום. נסו שוב או חפשו יישוב.",
        );
      },
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 10 * 60_000 },
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && results[active]) {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const listId = `${id}-list`;
  const showList = open && results.length > 0;

  return (
    <div className="relative rounded-2xl border border-line bg-card px-4 py-3 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <label
          htmlFor={`${id}-input`}
          className="flex items-center gap-1 text-xs font-medium text-muted"
        >
          <PinIcon className="size-3.5" />
          יישוב
        </label>
        <button
          type="button"
          onClick={locate}
          disabled={locating}
          className="inline-flex min-h-6 items-center px-1 text-xs font-medium text-accent underline-offset-2 hover:underline disabled:opacity-60"
        >
          {locating ? "מאתר…" : "המיקום שלי"}
        </button>
      </div>
      <input
        ref={inputRef}
        id={`${id}-input`}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `${id}-opt-${active}` : undefined}
        autoComplete="off"
        placeholder={locality.nameHe}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        className="mt-0.5 w-full bg-transparent text-lg font-medium placeholder:text-ink focus:placeholder:text-muted focus:outline-none"
      />
      <p role="status" aria-live="polite" className="text-xs text-muted empty:hidden">
        {status}
      </p>
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute inset-x-0 top-full z-10 mt-1 max-h-80 overflow-auto rounded-lg border border-line bg-card py-1 shadow-lg"
        >
          {results.map((l, i) => (
            <li
              key={l.code}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(l);
              }}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-baseline justify-between gap-3 px-4 py-2 ${
                i === active ? "bg-accent-soft" : ""
              }`}
            >
              <span>{l.nameHe}</span>
              {l.nameEn && (
                <span dir="ltr" className="text-xs text-muted">
                  {l.nameEn}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
